import Translations from "@translate/components/Member/CurrentPlan/subscriptionRetention/runCancelSubscriptionFlow";
import {
  MemberProductLevelEnum,
  SubscriptionCapability,
  type PaymentPlan,
} from "@features/api/memberApi";
import { I18nUtil } from "@utils/i18n";
import { ProductLevelType } from "../constants";
import type { SubscriptionListItem } from "../ManageBillingModal/types";
import type { openCancelFeedbackSurveyModal } from "./CancelFeedbackSurveyModalCreateAble";
import type { openRetentionCreditsOfferModal } from "./RetentionCreditsOfferModalCreateAble";

const I18n = I18nUtil.createScoped(Translations);

const COMPLETE_PLAN_MARK = "complete";

export interface CancelSubscriptionFlowDeps {
  /** 挽留赠送额度；> 0 才弹挽留 */
  retentionCredits: number | undefined;
  openRetentionCreditsOfferModal: typeof openRetentionCreditsOfferModal;
  openCancelFeedbackSurveyModal: typeof openCancelFeedbackSurveyModal;
  /** 能力含 CANCEL_AT_PERIOD_END 时的末步：PATCH auto_renew=false（失败时内部已 Toast，返回 null） */
  turnOffAutoRenew: (item: SubscriptionListItem) => Promise<PaymentPlan | null>;
  /** 旧末步：门户 / PayPal autopay / 应用商店提示 */
  legacyCancel: (item: SubscriptionListItem) => void;
  /** 关闭成功的提示 */
  onTurnedOff: (message: string) => void;
}

export type CancelSubscriptionFlowResult = "done" | "aborted" | "failed";

/** 挽留弹窗 / 关闭成功提示里的套餐名（沿用原 H5 管理页的推导） */
export function getRetentionPlanName(item: SubscriptionListItem): string {
  return item.id.includes(COMPLETE_PLAN_MARK)
    ? I18n.t("Ed Complete")
    : ProductLevelType[item.product_level || MemberProductLevelEnum.PRO];
}

/**
 * 关闭自动续费 / 取消订阅流程（EDG-552，fe-spec §3.4 ②、§4.6 F6）：
 * 挽留（有额度才弹）→ 原因问卷 → 末步按能力分流。PC 弹窗与 H5 管理页共用（PC 由此首次接入挽留 / 问卷）。
 * 任一步用户放弃返回 aborted；PATCH 失败返回 failed（行保持原状，AC-26）。
 */
export async function runCancelSubscriptionFlow(
  item: SubscriptionListItem,
  deps: CancelSubscriptionFlowDeps,
): Promise<CancelSubscriptionFlowResult> {
  const planName = getRetentionPlanName(item);
  if (typeof deps.retentionCredits === "number" && deps.retentionCredits > 0) {
    const retention = await deps.openRetentionCreditsOfferModal({ planName });
    if (retention.action !== "still_want_to_cancel") {
      return "aborted";
    }
  }
  const feedback = await deps.openCancelFeedbackSurveyModal();
  if (feedback.action !== "submit") {
    return "aborted";
  }
  if (
    !item.capabilities?.includes(SubscriptionCapability.CANCEL_AT_PERIOD_END)
  ) {
    deps.legacyCancel(item);
    return "done";
  }
  const updated = await deps.turnOffAutoRenew(item);
  if (!updated) {
    return "failed";
  }
  deps.onTurnedOff(
    I18n.t("Auto-renew is off. You'll keep %{plan} until %{date}.", {
      plan: planName,
      date: item.currentPeriodEndDate ?? "—",
    }),
  );
  return "done";
}
